/**
 * Microsoft Clarity: custom events and session tags, so recordings and heatmaps can be filtered by what a visitor
 * did (size picked, size guide opened, upsell added, checkout clicked). Clarity itself is loaded by the Clarity app
 * after consent; calls before that only go to its queue. Nothing happens without window.clarity.
 */
import { on } from '@lucid/core';
import { StandardEvents } from '@shopify/events';

const clarity = (...args) => {
  if (typeof window.clarity === 'function') window.clarity(...args);
};
const event = (name) => clarity('event', name);
const tag = (key, value) => {
  if (value !== undefined && value !== null && value !== '') clarity('set', key, String(value));
};

/* Session tags */
const body = document.body;
tag('template', body?.dataset.template);
tag('country', window.Shopify?.country);
tag('language', document.documentElement.lang);
tag('currency', window.Shopify?.currency?.active);
const dropState = document.querySelector('[data-drop-state]')?.getAttribute('data-drop-state');
tag('drop_state', dropState);

/* Size: picked by the visitor (not the preselect), missing when adding, size guide and size finder */
document.addEventListener(
  'change',
  (e) => {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || !e.isTrusted) return;
    if (input.closest('fieldset[data-lucid-pick], .lucid-sizes')) {
      event(input.closest('quick-add-component, dialog') ? 'size_selected_quick_add' : 'size_selected');
    } else if (input.closest('variant-picker')) {
      event('option_selected');
    }
  },
  true
);
on('size:prompt', () => event('size_missing_prompt'));
on('size:state', (e) => {
  if (e.detail?.unpicked) event('size_missing_prompt');
});
on('drawer:state', (e) => {
  const id = String(e.detail?.id || '');
  if (!e.detail?.open) return;
  if (id.startsWith('size-guide')) event('size_guide_open');
  else if (id === 'quick-size-guide') event('size_guide_open_quick_add');
  else if (id && id !== 'cart') event(`drawer_open_${id.replace(/[^a-z0-9]+/gi, '_')}`);
});
on('size-guide:apply', () => event('size_finder_apply'));

/* Cart: adds by source, removes, drawer, checkout */
document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
  const source = e.target instanceof Element ? e.target : null;
  if (e.action === 'remove') return event('cart_remove');
  if (e.action !== 'add') return event('cart_update');
  let where = 'pdp';
  if (source?.closest('.lucid-cart-set')) where = 'cart_set';
  else if (source?.closest('.lucid-cart-rail, [data-rail-wrapper]')) where = 'cart_upsell';
  else if (source?.closest('lucid-bundle, .lucid-bundle, .lucid-bundle-offer')) where = 'pdp_set';
  else if (source?.closest('quick-add-component, dialog')) where = 'quick_add';
  else if (source?.closest('.lucid-card')) where = 'card';
  else if (source?.closest('#cart-drawer')) where = 'cart';
  event(`add_to_cart_${where}`);
  clarity('upgrade', 'add_to_cart');
});
document.getElementById('cart-drawer')?.addEventListener('theme-drawer:open', () => event('cart_drawer_open'));
document.addEventListener(
  'click',
  (e) => {
    const target = e.target instanceof Element ? e.target : null;
    if (!target) return;
    if (target.closest('#checkout, [name="checkout"]')) {
      event(target.closest('#cart-drawer') ? 'checkout_click_drawer' : 'checkout_click_cart');
      clarity('upgrade', 'checkout_click');
    } else if (target.closest('.lucid-card__dot')) event('card_colour_dot');
    else if (target.closest('.lucid-washes__tile')) event('pdp_colour_switch');
    else if (target.closest('.lucid-services__item a')) event('service_link');
  },
  true
);

/* Back in stock, wishlist, filters */
on('bis:opened', () => event('bis_open'));
on('bis:subscribed', () => event('bis_subscribe'));
on('wishlist:change', (e) => {
  if (e.detail?.added === true) event('wishlist_add');
});
on('filter:open', () => event('filter_open'));
